import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { COORDINATE_SYSTEM, LayersList } from '@deck.gl/core/typed';
import { Store } from 'components/common/types/Store.types';
import { PlannerPoi } from 'components/common/types/Planner.types';
import { TouchedPoint } from 'components/common/types/CampaignPlan.types';
import { usePlannerSegmentsIndexRange } from 'components/pages/Planner/hooks/usePlannerSegmentsIndexRange';
import { DealStatus } from 'components/common/types/Deal.types';
import { getSelectedCluster } from 'store/carto/selectors';
import { getIsDirectSalesCampaignType } from 'store/dealManagement/selectors/getCampaignType';
import { getIsSharedCampaign, getSharedCampaignPlanMapAssetCount } from 'store/sharedCampaignPlan/selectors';
import { getDirectSalesStatus } from 'store/dealManagement/selectors/getDirectSalesStatus';
import { getCurrentLineId } from 'store/dealManagement/selectors/getCurrentLineId';
import { getCurrentLineAvailability } from 'store/dealManagement/selectors/availabilityAssets';
import { useCampaignPlanFiltersContext } from 'components/common/CampaignPlanFiltersContext';
import ClusterAssetsCompositeLayer from './Layer/ClusterAssetsCompositeLayer';
import POICompositeLayer from './Layer/POICompositeLayer';
import { getMapElementPosition } from './utils/getMapElementPosition';
import { usePlannerMap } from './usePlannerMap';
import { UsePlannerMapLayers } from './PlannerMap.types';
import { getMapElementSize } from './utils/getMapElementSize';
import { isDirectSalesLinePriced } from '../hooks/utils/getIsDirectSalesLinePriced';
import { useFeatureSelectionLayer } from './featureSelection/useFeatureSelectionLayer';
import { useEnableFeatureSelectionLayer } from './useEnableFeatureSelectionLayer';
import { getPoiElementRadius } from './utils/getPoiElementRadius';
import { isClusterClickable } from './utils/getIsMapElementClickable';
import { createSearchLocationPinLayer, SearchLocationPin } from './Layer/createSearchLocationPinLayer';

const INITIAL_RADIUS = 50;
const RADIUS_DECREASE_RATIO = 5;
const RADIUS_MIN_LIMIT = 20;
const LINE_INCLUDE_COLOR = [44, 36, 195];
const LINE_EXCLUDE_COLOR = [66, 65, 78];
const FILL_INCLUDE_COLOR = [44, 36, 195, 55];
const FILL_EXCLUDE_COLOR = [66, 65, 78, 55];

const touchedPointToMapPoi = (point: TouchedPoint, index: number): PlannerPoi => ({
  id: `touched-point-${index}`,
  name: '',
  geometry: { coordinates: [point.longitude, point.latitude] },
  properties: {
    categoryName: '',
    categoryType: '',
    address: '',
    postalCode: '',
    dataProvider: '',
    mediaOwner: '',
    type: '',
    visibility: { include: true, radius: point.radius },
  },
});

export const POI_LAYER_ID = 'poi-cluster-composite-layer';

export type UsePlannerMapLayersOptions = {
  isPoiMarkersVisible?: boolean;
  locationPin?: SearchLocationPin | null;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const usePlannerMapLayers = (options?: UsePlannerMapLayersOptions): UsePlannerMapLayers => {
  const isPoiMarkersVisible = options?.isPoiMarkersVisible ?? true;
  const locationPin = options?.locationPin ?? null;
  const isDirectSalesCampaignType = useSelector(getIsDirectSalesCampaignType);
  const {
    availableImpressions,
    allocatedImpressions,
    allocatedFrames,
    availableFrames,
    unavailableAssets = [],
    missingAssets = [],
  } = useSelector(getCurrentLineAvailability);
  const isSharedCampaign = useSelector(getIsSharedCampaign);
  const numberOfSharedCampaignAssets = useSelector(getSharedCampaignPlanMapAssetCount);
  const { activeColorFilter, touchedPoints } = useCampaignPlanFiltersContext();

  const currentLineId = useSelector(getCurrentLineId);
  const { frameIdIndexes, isAllocationClusterVisible, isImpressionsClusterVisible, isAvailabilityClusterVisible } =
    useSelector((state: Store) => state.dealManagement.plannerSettings);

  const { clusterAssetsData, poiData: basePOIData, handleViewportChange, lassoFurnitures } = usePlannerMap();

  const sharedCampaignPoiData = useMemo<PlannerPoi[]>(
    () => (isSharedCampaign ? touchedPoints.map(touchedPointToMapPoi) : []),
    [isSharedCampaign, touchedPoints],
  );

  const poiData = isSharedCampaign && sharedCampaignPoiData.length > 0 ? sharedCampaignPoiData : basePOIData;
  const rangeSegments = usePlannerSegmentsIndexRange(frameIdIndexes);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const status = useSelector(getDirectSalesStatus);
  const zoom = useSelector((state: Store) => state.dealManagement.plannerSettings.zoom);
  const hoveredFurnitureCode = useSelector((state: Store) => state.dealManagement.plannerSettings.hoveredFurnitureCode);

  const routeFrameCodes =
    useSelector((state: Store) =>
      isDirectSalesCampaignType
        ? state.dealManagement.commonDeal.currentLine.mapRouteFrameCodes
        : state.dealManagement.commonDeal.currentLine.routeFrameCodes,
    ) ?? [];

  const assetsView = useSelector((state: Store) => state.dealManagement.plannerSettings.assetsView);
  const selectedCluster = useSelector(getSelectedCluster);

  const { isEnabled: isFeatureSelectionLayerEnabled } = useEnableFeatureSelectionLayer();
  const isReadOnly = [DealStatus.ENDED, DealStatus.CANCELLED, DealStatus.TERMINATED, DealStatus.FAILED].includes(
    bookingStatusCode,
  );

  const poiHash = useMemo(() => {
    const includeCount = poiData.filter((poi) => poi.properties?.visibility?.include).length;
    const excludeCount = poiData.length - includeCount;
    return `${poiData.length}:${includeCount}:${excludeCount}:${poiData[0]?.name ?? ''}:${poiData.at(-1)?.name ?? ''}`;
  }, [poiData]);

  const assetsHash = useMemo(
    () =>
      `${clusterAssetsData.length}:${clusterAssetsData[0]?.properties?.minOfImpressions ?? ''}:${clusterAssetsData.at(-1)?.properties?.maxOfImpressions ?? ''}:${poiHash}`,
    [clusterAssetsData, poiHash],
  );

  const updateAssetsTriggers = useMemo(
    () => ({
      getAssetColor: [assetsHash, routeFrameCodes.length, activeColorFilter],
      getPosition: [assetsHash],
      getPixelOffset: [assetsHash],
      getFillColor: [assetsHash],
      getLineColor: [assetsHash],
      getRadius: [assetsHash],
      getIcon: [assetsHash, routeFrameCodes.length],
      getSize: [assetsHash, routeFrameCodes.length],
      getText: [assetsHash, routeFrameCodes.length],
    }),
    [assetsHash, routeFrameCodes.length, activeColorFilter],
  );

  const updatePoisTriggers = useMemo(
    () => ({
      getPosition: [poiHash],
      getPixelOffset: [poiHash],
    }),
    [poiHash],
  );

  const dynamicRadius = INITIAL_RADIUS - RADIUS_DECREASE_RATIO * zoom;
  const clusterRadius = Math.max(dynamicRadius, RADIUS_MIN_LIMIT);
  const totalFramesCount = assetsView === 'ALL' ? availableFrames + unavailableAssets.length : availableFrames;

  const lassoFurnitureCodes = useMemo(() => {
    if (!lassoFurnitures?.length) return null;

    const furnitureCodesSet = new Set<string>();

    lassoFurnitures.forEach((furniture) => {
      if (furniture.furnitureCode) {
        furnitureCodesSet.add(furniture.furnitureCode);
      }
    });

    return furnitureCodesSet;
  }, [lassoFurnitures]);

  const clusterAssetsLayer = useMemo(() => {
    const maxAssets = isSharedCampaign
      ? numberOfSharedCampaignAssets
      : missingAssets?.length ||
        allocatedFrames ||
        (assetsView === 'SELECTED' ? routeFrameCodes.length : totalFramesCount);

    return new ClusterAssetsCompositeLayer({
      id: 'cluster-assets-composite-layer',
      data: clusterAssetsData,
      routeFrameCodes,
      impressions: {
        availableImpressions,
        allocatedImpressions,
      },
      clusters: {
        isAllocationClusterVisible,
        isImpressionsClusterVisible,
        isAvailabilityClusterVisible,
      },
      getPosition: getMapElementPosition,
      getMapElementSize: getMapElementSize(1, maxAssets),
      onViewportDataChange: handleViewportChange,
      sizeScale: 1,
      coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
      rangeSegments,
      isReadOnly,
      isDirectSales: isDirectSalesCampaignType,
      isPriced: !isSharedCampaign && isDirectSalesLinePriced(status),
      isSharedCampaign,
      activeColorFilter,
      updateTriggers: updateAssetsTriggers,
      clusterRadius,
      selectedCluster,
      isClusterClickable,
      hoveredFurnitureCode,
      lassoFurnitureCodes,
    });
  }, [
    availableImpressions,
    allocatedImpressions,
    allocatedFrames,
    status,
    isAllocationClusterVisible,
    isImpressionsClusterVisible,
    isAvailabilityClusterVisible,
    handleViewportChange,
    isReadOnly,
    clusterAssetsData,
    rangeSegments,
    missingAssets.length,
    assetsView,
    selectedCluster,
    routeFrameCodes.length,
    totalFramesCount,
    isDirectSalesCampaignType,
    updateAssetsTriggers,
    clusterRadius,
    hoveredFurnitureCode,
    lassoFurnitureCodes,
    isSharedCampaign,
    numberOfSharedCampaignAssets,
    activeColorFilter,
  ]);

  const getLineColor = (data: { properties?: { visibility?: { include: boolean } } }): number[] => {
    return data.properties?.visibility?.include ? LINE_INCLUDE_COLOR : LINE_EXCLUDE_COLOR;
  };

  const getFillColor = (data: { properties?: { visibility?: { include: boolean } } }): number[] => {
    return data.properties?.visibility?.include ? FILL_INCLUDE_COLOR : FILL_EXCLUDE_COLOR;
  };

  const poiLayer = useMemo(
    () =>
      new POICompositeLayer({
        id: POI_LAYER_ID,
        data: poiData,
        visible: isPoiMarkersVisible,
        getPosition: getMapElementPosition,
        getRadius: getPoiElementRadius,
        getLineColor,
        getFillColor,
        sizeScale: 1,
        clusterRadius: 1,
        coordinateSystem: COORDINATE_SYSTEM.LNGLAT,
        updateTriggers: updatePoisTriggers,
      }),
    [poiData, updatePoisTriggers, isPoiMarkersVisible],
  );

  const widgetsLayers = useFeatureSelectionLayer(currentLineId, { isEnabled: isFeatureSelectionLayerEnabled });

  const searchLocationPinLayer = useMemo(() => createSearchLocationPinLayer(locationPin), [locationPin]);

  const mapLayers = useMemo<LayersList>(() => {
    return [
      ...widgetsLayers,
      ...(poiData?.length ? [poiLayer] : []),
      ...(clusterAssetsData?.length ? [clusterAssetsLayer] : []),
      ...(searchLocationPinLayer ? [searchLocationPinLayer] : []),
    ];
  }, [widgetsLayers, poiData?.length, clusterAssetsData?.length, poiLayer, clusterAssetsLayer, searchLocationPinLayer]);

  return { mapLayers, lassoFurnitures, clusterAssetsData };
};
